import React, {forwardRef, useCallback} from 'react';
import {useMaskito} from '@maskito/react';
import {maskitoDate} from '@maskito/kit';
import {Cancel} from '@mui/icons-material';
import {IconButton, InputAdornment, OutlinedInput} from '@mui/material';
import {styled} from '@mui/material/styles';
import {CalendarIcon} from '../../../../assets/icons/calendarIcon';

type InputProps = {
  selectedValue: string;
  setValue: (date: Date | null) => void;
  onChangeDateValue: (date: string) => void;
  placeholder?: string;
};

const StyledClearInputButton = styled(IconButton)(({theme}) => ({
  '&.MuiButtonBase-root': {
    marginRight: theme.spacing(1),
  },
}));

const StyledInputAdornment = styled(InputAdornment)({
  margin: 'auto',
});

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {setValue, placeholder, selectedValue, onChangeDateValue, ...props},
    ref
  ) => {
    const onClearFiled = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        setValue(null);
      },
      [setValue]
    );

    const maskOptions = maskitoDate({
      mode: 'mm/dd/yyyy',
      separator: '/',
    });

    const maskedInputRef = useMaskito({options: maskOptions});

    return (
      <OutlinedInput
        {...props}
        inputProps={{
          placeholder,
          'data-testid': 'date-range-picker-input',
          ref: maskedInputRef,
        }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          onChangeDateValue(e.target.value);
        }}
        endAdornment={
          <StyledInputAdornment position="end">
            {selectedValue ? (
              <StyledClearInputButton
                onClick={onClearFiled}
                size="small"
                data-testid="picker-clear-date-btn"
              >
                <Cancel fontSize="small" />
              </StyledClearInputButton>
            ) : null}
            <CalendarIcon />
          </StyledInputAdornment>
        }
        ref={ref}
      />
    );
  }
);

Input.displayName = 'Input';

export default Input;
